import {
  ColumnData,
  IntegrationDigestAsset,
  IntegrationDigestRow,
  SlideAction,
  SlidePanelRegistrar,
  tokens,
  useSlidePanel,
} from '@gripp/shared-ui';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import {
  useNavigate,
  useOutletContext,
  useParams,
} from 'react-router-dom';
import { AssetRouteProvider } from '../../assets/assetRouteContext';
import { AssetCard } from '../../assets/components/assetCard';
import { AssetFilters } from '../../assets/components/assetFilters';

const INTEGRATION_DIGEST_PANEL_SOURCE =
  'gripp-workspace/apps/gripp-web/src/activity/components/integrationDigestPanel.tsx';
const ACTIVITY_ROUTES_SOURCE =
  'gripp-workspace/apps/gripp-web/src/activity/routes.tsx';
const ACTIVITY_DIGEST_ROUTE_PATTERN = '/activities/digest/:digestKey';
const ACTIVITY_DIGEST_ASSET_ROUTE_PATTERN =
  '/activities/digest/:digestKey/asset/:assetId';
const ASSET_CARD_COMPONENT_SOURCE =
  'gripp-workspace/apps/gripp-web/src/assets/components/assetCard.tsx';

/**
 * Outlet-context shape published by `Activities` via
 * `PanelShell.outletContextExtras`. The digest detail panel reads the already-
 * fetched digest rows here rather than re-issuing the `integration.syncDigests`
 * query for a single row — the rows are unpaginated and already in memory on
 * the feed.
 */
type DigestOutletContext = SlidePanelRegistrar & {
  digestRows?: IntegrationDigestRow[];
};

/**
 * Right-hand SlideLayout detail panel for an integration sync digest.
 *
 * Replaces the former expand-in-place per-asset list. Tapping a digest row in
 * `IntegrationDigestSection` navigates to `activities/digest/:digestKey`, which
 * renders this component through the activity `<Outlet>`. It registers the
 * right column via `useSlidePanel('right', …)` — exactly the mechanism the
 * activity conversation panel uses for a selected issue — so the digest detail
 * slides in with the same transition and back-button affordance.
 *
 * Header: `showHeader` + `showBackButton` + `onBackButtonPress`, styled by
 * SlideLayout identically to the asset card header in the conversation panel
 * (back chevron + title). Body: the per-asset breakdown; tapping an asset row
 * navigates to that asset's detail page.
 */
export const IntegrationDigestPanel = () => {
  const ctx = useOutletContext<DigestOutletContext | null>();
  const { digestKey: rawDigestKey, assetId } = useParams();
  const navigate = useNavigate();

  const digestKey = rawDigestKey
    ? decodeURIComponent(rawDigestKey)
    : undefined;
  // The raw (still-encoded) key is what the URL carries; build child links from
  // it so the encoded pipe (`day|provider` -> `%7C`) round-trips through the
  // nested asset route unchanged.
  const encodedDigestKey = rawDigestKey ?? '';

  const digest = useMemo(
    () => ctx?.digestRows?.find((row) => row.digestKey === digestKey),
    [ctx?.digestRows, digestKey]
  );

  const onAssetSelected = useMemo(
    () => (selectedAssetId: string) =>
      navigate(`/activities/digest/${encodedDigestKey}/asset/${selectedAssetId}`, {
        state: { slideLayoutInitialTransition: SlideAction.NEXT },
      }),
    [navigate, encodedDigestKey]
  );

  const rightColumn = useMemo<ColumnData | null>(() => {
    if (!digestKey) return null;
    return {
      title: 'integrations.digest.detailTitle',
      showHeader: true,
      showBackButton: true,
      onBackButtonPress: () => navigate('/activities'),
      disableScroll: false,
      mainComponent: (
        <DigestAssetList
          assets={digest?.assets ?? []}
          onAssetSelected={onAssetSelected}
        />
      ),
      debug: {
        id: 'activity-digest:right-detail',
        titleKey: 'integrations.digest.detailTitle',
        component: 'IntegrationDigestPanel',
        componentSource: INTEGRATION_DIGEST_PANEL_SOURCE,
        columnSource: INTEGRATION_DIGEST_PANEL_SOURCE,
        ownerComponent: 'IntegrationDigestPanel',
        ownerSource: INTEGRATION_DIGEST_PANEL_SOURCE,
        routePanelKey: 'activity-digest',
        routePattern: ACTIVITY_DIGEST_ROUTE_PATTERN,
        routeSource: ACTIVITY_ROUTES_SOURCE,
        entity: { type: 'digest', id: digestKey },
      },
    };
  }, [digestKey, digest, navigate, onAssetSelected]);

  // Asset opened from the digest panel renders as the third column — exactly
  // like the conversation panel's asset card. Its back arrow returns to the
  // digest panel (`activities/digest/:digestKey`), not `/assets`.
  const thirdColumn = useMemo<ColumnData | null>(() => {
    if (!assetId || !digestKey) return null;
    const assetBasePath = `/activities/digest/${encodedDigestKey}/asset/${assetId}`;
    return {
      title: 'assets.menu.titleSingular',
      showHeader: true,
      showBackButton: true,
      onBackButtonPress: () =>
        navigate(`/activities/digest/${encodedDigestKey}`),
      headerComponent: <AssetFilters assetId={assetId} />,
      disableScroll: false,
      // Wrap in AssetRouteProvider so fold components inside AssetCard read
      // basePath/listPath via useAssetRouting() — `View all` then navigates to
      // `${basePath}/activity/all` (the digest-context AssetDetail route) and
      // `listPath` keeps issue-delete returning to /activities.
      mainComponent: (
        <AssetRouteProvider
          value={{
            basePath: assetBasePath,
            listPath: '/activities',
            isWorkspaceMode: false,
            issueId: undefined,
          }}
        >
          <AssetCard assetId={assetId} />
        </AssetRouteProvider>
      ),
      debug: {
        id: 'activity-digest:third-asset-card',
        titleKey: 'assets.menu.titleSingular',
        component: 'AssetCard',
        componentSource: ASSET_CARD_COMPONENT_SOURCE,
        columnSource: INTEGRATION_DIGEST_PANEL_SOURCE,
        ownerComponent: 'IntegrationDigestPanel',
        ownerSource: INTEGRATION_DIGEST_PANEL_SOURCE,
        routePanelKey: 'activity-digest-asset-card',
        routePattern: ACTIVITY_DIGEST_ASSET_ROUTE_PATTERN,
        routeSource: ACTIVITY_ROUTES_SOURCE,
        entity: { type: 'asset', id: assetId },
      },
    };
  }, [assetId, digestKey, encodedDigestKey, navigate]);

  useSlidePanel('right', rightColumn, [rightColumn]);
  useSlidePanel('third', thirdColumn, [thirdColumn]);

  return null;
};

type DigestAssetListProps = {
  assets: IntegrationDigestAsset[];
  onAssetSelected: (assetId: string) => void;
};

/**
 * Per-asset breakdown rendered in the digest detail panel body. Each row shows
 * the asset name and its readings (and faults, when > 0) for the day; tapping a
 * row opens that asset in the third column under the digest context (so its
 * back arrow returns to the digest panel) — mirroring the conversation panel's
 * asset hop.
 */
const DigestAssetList = ({ assets, onAssetSelected }: DigestAssetListProps) => {
  const { t } = useTranslation();

  if (assets.length === 0) {
    return (
      <View style={styles.emptyState}>
        <Text style={styles.emptyText}>
          {t('integrations.digest.detailEmpty')}
        </Text>
      </View>
    );
  }

  return (
    <View style={styles.list}>
      {assets.map((asset) => (
        <Pressable
          key={asset.assetId}
          testID={`integration-digest-asset-${asset.assetId}`}
          style={styles.assetRow}
          accessibilityRole="button"
          accessibilityLabel={`Open asset ${asset.assetName}`}
          onPress={() => onAssetSelected(asset.assetId)}
        >
          <View style={styles.assetNameColumn}>
            <Text style={styles.assetName} numberOfLines={1}>
              {asset.assetName}
            </Text>
          </View>
          <Text style={styles.assetMeta}>
            {t('integrations.digest.readingsCount', {
              count: asset.readingsWritten,
            })}
            {asset.issueCount > 0
              ? ` · ${t('integrations.digest.faultsCount', {
                  count: asset.issueCount,
                })}`
              : ''}
          </Text>
        </Pressable>
      ))}
    </View>
  );
};

const styles = StyleSheet.create({
  list: {
    paddingVertical: tokens.spacing.xs,
  },
  assetRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: tokens.spacing.md,
    paddingHorizontal: tokens.spacing.lg,
    gap: tokens.spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: tokens.colors.neutral[200],
  },
  assetNameColumn: {
    flex: 1,
    minWidth: 0,
  },
  assetName: {
    ...tokens.typography.label.largeEmphasised,
    color: tokens.colors.neutral[600],
  },
  assetMeta: {
    ...tokens.typography.label.medium,
    color: tokens.colors.neutral[500],
    flexShrink: 0,
  },
  emptyState: {
    padding: tokens.spacing.lg,
  },
  emptyText: {
    ...tokens.typography.label.medium,
    color: tokens.colors.neutral[500],
  },
});
